import { type UseMutationOptions, useMutation } from '@tanstack/react-query';
import { client } from '../../client';
import type { BenchmarkView } from '../../queries/search-benchmarks';

/*
 * Hand-written stand-in for the create benchmark endpoint. The endpoint now
 * exists in the API and the URL and fields below match its contract; this file
 * mirrors the shape of the generated mutation wrappers and gets replaced by the
 * generated SDK function and types once `pnpm generate` can see the benchmark
 * routes. Callers only ever see the types exported here, so that swap is a
 * one-file change. Tests answer this path with the MSW handler in
 * `tests/mocks/handlers.ts`.
 */

const CREATE_BENCHMARK_URL = '/v1/workspaces/{workspace_id}/benchmarks';

export interface CreateBenchmarkRequest {
  name: string;
  description?: string | null;
  /** Hero or Carousel: the image type the benchmark grades, locked after creation. */
  scope: BenchmarkView['scope'];
}

export type CreateBenchmarkMutationVariables = { workspace_id: string } & CreateBenchmarkRequest;

export type CreateBenchmarkMutation = BenchmarkView;

async function createBenchmark({ workspace_id, ...body }: CreateBenchmarkMutationVariables) {
  const { data } = await client.post<{ 200: CreateBenchmarkMutation; 201: CreateBenchmarkMutation }, unknown, true>({
    url: CREATE_BENCHMARK_URL,
    path: { workspace_id },
    body,
    throwOnError: true,
  });
  return data;
}

export const useCreateBenchmarkMutation = <TError = unknown, TContext = unknown>(
  options?: Omit<
    UseMutationOptions<CreateBenchmarkMutation, TError, CreateBenchmarkMutationVariables, TContext>,
    'mutationKey' | 'mutationFn'
  >,
) => {
  return useMutation<CreateBenchmarkMutation, TError, CreateBenchmarkMutationVariables, TContext>({
    mutationKey: ['CreateBenchmark'],
    mutationFn: createBenchmark,
    ...options,
  });
};

useCreateBenchmarkMutation.fetcher = (variables: CreateBenchmarkMutationVariables) => () => createBenchmark(variables);
